// reportPhotos — what the server will accept on a problem report, declared on
// this side of the wire so a crew member is told BEFORE the upload, not after.
//
// Same shape as uploads.js: the rules a server file actually enforces, in one
// place, with a test that reads that server file and fails when the two
// disagree (server/utils/__tests__/feedbackPhotoLimits.test.js). Hardcoding
// these numbers into the screen instead is how a screen ends up offering a
// button the server answers 400 to — CLAUDE.md says so in those words.
//
// ── Why the client has to enforce the TOTAL, not just each picture ──────────
//
// The pictures travel as base64 inside the JSON body of POST /api/feedback,
// and server/server.js mounts `express.json({ limit: '10mb' })`. base64 is 4
// bytes for every 3, so four pictures at the 3 MB each cap is a ~16 MB body —
// and a 413 from the BODY PARSER happens before routes/feedback.js runs at
// all. Nothing in that route can be kind about it, the careful "a bad picture
// never loses the report" handling never executes, and the worker's typed
// description dies with the photos.
//
// So MAX_TOTAL_BYTES is not a tidiness rule. It is the thing standing between
// a four-photo report and a silent 413, and it has to be enforced HERE,
// before send, because by the time the server sees it it is too late.

/** `MAX_PHOTOS` in server/utils/feedbackScreenshot.js. */
export const MAX_PHOTOS = 4;

/** `MAX_BYTES` in server/utils/feedbackScreenshot.js — the per-picture cap. */
export const MAX_PHOTO_BYTES = 3 * 1024 * 1024;

/** `MAX_TOTAL_BYTES` in server/utils/feedbackScreenshot.js — the whole set. */
export const MAX_TOTAL_BYTES = 6 * 1024 * 1024;

/** A size a person reads. Never "0 MB" for a picture that exists. */
export function describeBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

/** What is already attached, in bytes. */
export function totalBytes(photos) {
  return (Array.isArray(photos) ? photos : []).reduce((n, p) => n + (Number(p && p.bytes) || 0), 0);
}

/**
 * Why this picture cannot be added to the ones already attached — as a
 * sentence a crew member can act on — or null.
 *
 * Every sentence names the limit AND what to do about it. "Too many pictures"
 * on its own leaves somebody tapping a control that does nothing.
 *
 * @param {{bytes: number}} photo   the DOWNSCALED picture, not the original file
 * @param {Array} photos            what is already attached
 */
export function refusePhoto(photo, photos = []) {
  const already = Array.isArray(photos) ? photos : [];
  if (already.length >= MAX_PHOTOS) {
    return `You can send ${MAX_PHOTOS} pictures with one report. Remove one to add another.`;
  }
  const bytes = Number(photo && photo.bytes) || 0;
  if (bytes > MAX_PHOTO_BYTES) {
    return `That picture is ${describeBytes(bytes)} and the limit is ${describeBytes(MAX_PHOTO_BYTES)}. Try a photo rather than a long screen recording.`;
  }
  if (totalBytes(already) + bytes > MAX_TOTAL_BYTES) {
    return `That would make the pictures too big to send together (the limit is ${describeBytes(MAX_TOTAL_BYTES)} in total). Remove one first.`;
  }
  return null;
}

/** Is there room for another? Used to word the hint, never to hide the picker. */
export function roomForMore(photos = []) {
  const already = Array.isArray(photos) ? photos : [];
  return already.length < MAX_PHOTOS && totalBytes(already) < MAX_TOTAL_BYTES;
}
